import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  getStockLevels, getStockDetail, saveAdjustment, reconcileStock, deleteAdjustment
} from '../../actions/meal_stock.actions';

const useStyles = makeStyles((theme) => ({
  stockCell: { fontWeight: 'bold', fontSize: '1.1em' },
  positive: { color: '#2e7d32' },
  negative: { color: '#c62828' },
  future: { color: '#999', fontStyle: 'italic' },
  detailBox: { margin: theme.spacing(1, 2) },
  muted: { color: '#888' }
}));

const fmtDate = d => {
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};

const emptyDialog = { open: false, mealId: null, mealName: '', mode: 'delta', quantity: '', target: '', reason: '' };

const MealStockPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const stock = useSelector(state => state.stock);

  const [expanded, setExpanded] = useState(null);
  const [dialog, setDialog] = useState(emptyDialog);

  // Stock is time-sensitive and derived — fetch fresh on every visit.
  useEffect(() => {
    dispatch(getStockLevels());
  }, [dispatch]);

  const toggleRow = mealId => {
    if (expanded === mealId) {
      setExpanded(null);
    } else {
      setExpanded(mealId);
      dispatch(getStockDetail(mealId));
    }
  };

  const openDialog = row => {
    setDialog({ ...emptyDialog, open: true, mealId: row.mealId, mealName: row.name });
  };

  const closeDialog = () => setDialog(emptyDialog);

  const submitDialog = () => {
    if (dialog.mode === 'set') {
      dispatch(reconcileStock(dialog.mealId, Number(dialog.target)));
    } else {
      dispatch(saveAdjustment({
        mealId: dialog.mealId,
        quantity: Number(dialog.quantity),
        reason: dialog.reason
      }));
    }
    closeDialog();
  };

  const removeEntry = (entry, mealId) => {
    dispatch(deleteAdjustment(entry.id, mealId));
  };

  const signClass = n => (n > 0 ? classes.positive : n < 0 ? classes.negative : '');

  const records = stock.records || [];

  return (
    <>
      <Typography variant="h6" style={{ margin: '8px 0' }}>Cooked Meal Stock</Typography>
      {records.length === 0 &&
        <Typography variant="body2" className={classes.muted} style={{ margin: 8 }}>
          No meals are set to track stock yet. Open a meal in the Menu and switch on
          “Track cooked stock” to see it here.
        </Typography>}

      {records.length > 0 &&
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Meal</TableCell>
                <TableCell align="right">From plans</TableCell>
                <TableCell align="right">Adjustments</TableCell>
                <TableCell align="right">In stock</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(row => {
                const detail = stock.details[row.mealId];
                const isOpen = expanded === row.mealId;
                return (
                  <React.Fragment key={row.mealId}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => toggleRow(row.mealId)}>
                          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="right">{row.madeMinusConsumed}</TableCell>
                      <TableCell align="right" className={signClass(row.adjustmentsTotal)}>
                        {row.adjustmentsTotal > 0 ? '+' : ''}{row.adjustmentsTotal}
                      </TableCell>
                      <TableCell align="right"
                        className={`${classes.stockCell} ${signClass(row.currentStock)}`}>
                        {row.currentStock}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" color="primary" onClick={() => openDialog(row)}>
                          Adjust
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box className={classes.detailBox}>
                            <Typography variant="subtitle2" gutterBottom>History</Typography>
                            {!detail && <Typography variant="body2" className={classes.muted}>Loading…</Typography>}
                            {detail && detail.length === 0 &&
                              <Typography variant="body2" className={classes.muted}>No history yet.</Typography>}
                            {detail && detail.length > 0 &&
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell align="right">Change</TableCell>
                                    <TableCell align="right">Balance</TableCell>
                                    <TableCell />
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {detail.map((e, i) => (
                                    <TableRow key={e.type + e.id + i}
                                      className={e.future ? classes.future : ''}>
                                      <TableCell>{fmtDate(e.date)}{e.future ? ' (planned)' : ''}</TableCell>
                                      <TableCell>
                                        {e.type === 'plan'
                                          ? `Cooked ${e.made}, ate ${e.consumed}`
                                          : `Adjustment${e.reason ? ' — ' + e.reason : ''}`}
                                      </TableCell>
                                      <TableCell align="right" className={signClass(e.delta)}>
                                        {e.delta > 0 ? '+' : ''}{e.delta}
                                      </TableCell>
                                      <TableCell align="right">{e.balance}</TableCell>
                                      <TableCell align="right">
                                        {e.type === 'adjustment' &&
                                          <IconButton size="small"
                                            onClick={() => removeEntry(e, row.mealId)}>
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>}

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust stock — {dialog.mealName}</DialogTitle>
        <DialogContent>
          <RadioGroup value={dialog.mode}
            onChange={e => setDialog({ ...dialog, mode: e.target.value })}>
            <FormControlLabel value="delta" control={<Radio />}
              label="Add / remove portions" />
            <FormControlLabel value="set" control={<Radio />}
              label="Set actual count (reconcile)" />
          </RadioGroup>

          {dialog.mode === 'delta' &&
            <>
              <TextField fullWidth type="number" label="Change (+ added, − removed)"
                value={dialog.quantity}
                onChange={e => setDialog({ ...dialog, quantity: e.target.value })} />
              <TextField fullWidth label="Reason (optional)" value={dialog.reason}
                onChange={e => setDialog({ ...dialog, reason: e.target.value })}
                style={{ marginTop: 8 }} />
            </>}

          {dialog.mode === 'set' &&
            <TextField fullWidth type="number" label="Actual portions in stock"
              value={dialog.target}
              onChange={e => setDialog({ ...dialog, target: e.target.value })}
              helperText="A single correcting adjustment will be created to match this count." />}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="secondary">Cancel</Button>
          <Button onClick={submitDialog} color="primary"
            disabled={dialog.mode === 'delta' ? dialog.quantity === '' : dialog.target === ''}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MealStockPage;
